import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Select as MantineSelect, Loader, type OptionsFilter, type SelectProps as MantineSelectProps } from '@mantine/core';
import { useDebounce } from '../../hooks/useDebounce';
import './SearchableAsyncSelect.css';

export interface AsyncSelectOption<TMeta = unknown> {
  value: string;
  label: string;
  meta?: TMeta;
}

export interface SearchableAsyncSelectProps<TMeta = unknown>
  extends Omit<
    MantineSelectProps,
    | 'data'
    | 'searchable'
    | 'onChange'
    | 'value'
    | 'filter'
    | 'searchValue'
    | 'onSearchChange'
    | 'error'
  > {
  error?: string;
  nothingFound?: ReactNode;
  /** Загрузка опций по строке поиска (уже после debounce и проверки minQueryLength). */
  loadOptions: (query: string) => Promise<AsyncSelectOption<TMeta>[]>;
  /** Минимальная длина запроса до запроса на сервер (по умолчанию 3). */
  minQueryLength?: number;
  /** Debounce ввода перед вызовом loadOptions (по умолчанию 300 мс). */
  debounceMs?: number;
  value?: string | null;
  onChange?: (value: string | null) => void;
  /** Вызывается при выборе пункта из списка (вместе с onChange), с полным объектом опции. */
  onOptionSelect?: (option: AsyncSelectOption<TMeta>) => void;
}

export default function SearchableAsyncSelect<TMeta = unknown>({
  loadOptions,
  minQueryLength = 3,
  debounceMs = 300,
  value,
  onChange,
  onOptionSelect,
  onBlur,
  error,
  nothingFound,
  nothingFoundMessage,
  className = '',
  disabled,
  ...rest
}: SearchableAsyncSelectProps<TMeta>) {
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, debounceMs);
  const [data, setData] = useState<AsyncSelectOption<TMeta>[]>([]);
  const [loading, setLoading] = useState(false);
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;
  const optionsRef = useRef<AsyncSelectOption<TMeta>[]>([]);

  useEffect(() => {
    if (value) {
      setSearchValue(value);
    }
  }, [value]);

  useEffect(() => {
    const q = debouncedSearch.trim();
    if (q.length < minQueryLength) {
      setData([]);
      optionsRef.current = [];
      return;
    }

    let cancelled = false;
    setLoading(true);
    void loadOptionsRef
      .current(q)
      .then((items) => {
        if (cancelled) return;
        optionsRef.current = items;
        setData(items);
      })
      .catch(() => {
        if (cancelled) return;
        optionsRef.current = [];
        setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, minQueryLength]);

  const dataForSelect = useMemo(() => {
    const rows = data.map((item) => ({ value: item.value, label: item.label }));
    if (!value) return rows;
    if (rows.some((r) => r.value === value)) return rows;
    return [{ value, label: value }, ...rows];
  }, [data, value]);

  const optionsFilter: OptionsFilter = useMemo(
    () =>
      ({ options }) => {
        if (searchValue.trim().length < minQueryLength) return options;
        const fromApi = new Set(data.map((item) => item.value));
        return options.filter(
          (option) => 'value' in option && fromApi.has(String(option.value)),
        );
      },
    [data, minQueryLength, searchValue],
  );

  const selectClass = `searchable-async-select ${error ? 'searchable-async-select--error' : ''} ${className}`.trim();

  return (
    <div className="searchable-async-select-wrapper">
      <MantineSelect
        className={selectClass}
        searchable
        filter={optionsFilter}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        data={dataForSelect}
        value={value ?? null}
        onChange={(selected) => {
          if (selected != null) {
            const opt =
              optionsRef.current.find((o) => o.value === selected) ??
              ({ value: selected, label: selected } as AsyncSelectOption<TMeta>);
            // Иначе searchable Mantine оставляет в инпуте набранный текст («ленина 89»).
            setSearchValue(opt.label);
            onChange?.(selected);
            onOptionSelect?.(opt);
          } else {
            setSearchValue('');
            onChange?.(selected);
          }
        }}
        onBlur={onBlur}
        error={error}
        nothingFoundMessage={nothingFoundMessage ?? nothingFound}
        rightSection={loading ? <Loader size="xs" /> : undefined}
        rightSectionPointerEvents="none"
        disabled={disabled}
        {...rest}
      />
    </div>
  );
}
